import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EvaluationsTable } from '@/components/organisms/EvaluationsTable';
import { useModuleFilter } from '@/context/ModuleFilter';

import { EvaluationsLoading } from './Evaluations.loading';
import { useEvaluations, useJudges } from './Evaluations.requests';
import { styles } from './Evaluations.styles';

type VerdictFilter = 'all' | 'passed' | 'failed';

export const Evaluations = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'evaluations' });
	const { selectedModule } = useModuleFilter();
	const [judgeId, setJudgeId] = useState<string>('');
	const [verdict, setVerdict] = useState<VerdictFilter>('all');

	const judgesQuery = useJudges({ moduleName: selectedModule });
	const evalsQuery = useEvaluations({
		judgeId: judgeId || undefined,
		verdict: verdict === 'all' ? undefined : verdict,
		moduleName: selectedModule,
	});

	if (evalsQuery.isLoading) return <EvaluationsLoading />;
	if (evalsQuery.isError || !evalsQuery.data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const rows = evalsQuery.data.items;

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUBTITLE')}</p>
					</div>
					<div className="flex items-center gap-xs">
						<select
							value={judgeId}
							onChange={(e) => setJudgeId(e.target.value)}
							className="rounded-md border border-subtle bg-white px-xs py-2xs text-[13px] text-default focus:outline-none"
						>
							<option value="">{t('FILTER_ALL_JUDGES')}</option>
							{judgesQuery.data?.items.map((j) => (
								<option key={j.id} value={j.id}>
									{j.name}
								</option>
							))}
						</select>
						<select
							value={verdict}
							onChange={(e) => setVerdict(e.target.value as VerdictFilter)}
							className="rounded-md border border-subtle bg-white px-xs py-2xs text-[13px] text-default focus:outline-none"
						>
							<option value="all">{t('FILTER_ALL_VERDICTS')}</option>
							<option value="passed">{t('FILTER_VERDICT_PASSED')}</option>
							<option value="failed">{t('FILTER_VERDICT_FAILED')}</option>
						</select>
					</div>
				</header>

				{rows.length === 0 ? (
					<section className="flex flex-col items-start gap-xs rounded-md border border-subtle bg-grey-25 px-xl py-xl">
						<p className="text-[14px] font-semibold text-default">{t('EMPTY_TITLE')}</p>
						<p className="text-[12px] text-subtle">{t('EMPTY_BODY')}</p>
					</section>
				) : (
					<EvaluationsTable rows={rows} />
				)}
			</main>
		</>
	);
};
